import { useId, useState } from "react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GST_STATES } from "@/modules/settings/gstStates";

interface CompanyDraft {
  legalName: string;
  tradeName: string;
  gstin: string;
  registeredStateCode: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  pincode: string;
  invoicePrefix: string;
}

const BLANK_DRAFT: CompanyDraft = {
  legalName: "",
  tradeName: "",
  gstin: "",
  registeredStateCode: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  pincode: "",
  invoicePrefix: "",
};

/**
 * The company and GST settings form (M8-14, FR-GST-1).
 *
 * Held in local state and nowhere else: `SettingsRepository` has no method
 * for company settings yet, there is no fixture for it either, and a form
 * that posted to nothing on submit would be worse than one that says so —
 * see the note under the form. Persistence is a later ticket; this is the
 * shape it will save.
 *
 * The registered state is the one field on this form that is not just
 * record-keeping. Every invoice compares it against the order's own
 * shipping state to decide the tax split (FR-GST-4): the same state means
 * CGST + SGST, a different one means IGST. Get this field wrong once and
 * every invoice issued against it is wrong the same way, silently, until
 * someone notices the GST return does not reconcile.
 */
export function CompanySettings() {
  const [draft, setDraft] = useState<CompanyDraft>(BLANK_DRAFT);

  const legalNameId = useId();
  const tradeNameId = useId();
  const gstinId = useId();
  const stateId = useId();
  const line1Id = useId();
  const line2Id = useId();
  const cityId = useId();
  const pincodeId = useId();
  const prefixId = useId();

  function field<K extends keyof CompanyDraft>(key: K) {
    return {
      value: draft[key],
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        setDraft((prev) => ({ ...prev, [key]: event.target.value }));
      },
    };
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div className="rounded-md border border-dashed p-3 text-sm">
        <p className="font-medium">Nothing here saves yet.</p>
        <p className="text-muted-foreground">
          There is no company-settings repository method to write to — this form
          holds what is typed in component state only, and it is gone on the
          next reload. It exists to show the shape FR-GST-1 asks for before
          there is somewhere to send it.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Company</CardTitle>
          <CardDescription>
            The legal identity this business invoices under.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor={legalNameId}>Legal name</Label>
            <Input id={legalNameId} {...field("legalName")} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={tradeNameId}>Trade name</Label>
            <Input id={tradeNameId} {...field("tradeName")} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={gstinId}>GSTIN</Label>
            <Input
              id={gstinId}
              className="font-mono tabular-nums"
              maxLength={15}
              {...field("gstin")}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={stateId}>Registered state</Label>
            <Select
              value={draft.registeredStateCode}
              onValueChange={(value) => {
                setDraft((prev) => ({ ...prev, registeredStateCode: value }));
              }}
            >
              <SelectTrigger id={stateId} className="w-full">
                <SelectValue placeholder="Select a state" />
              </SelectTrigger>
              <SelectContent>
                {GST_STATES.map((state) => (
                  <SelectItem key={state.code} value={state.code}>
                    {state.name} ({state.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              Compared against each order&apos;s shipping state to decide CGST +
              SGST (same state) or IGST (different state) — FR-GST-4.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor={prefixId}>Invoice prefix</Label>
            <Input
              id={prefixId}
              placeholder="e.g. EST"
              {...field("invoicePrefix")}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Registered address</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor={line1Id}>Address line 1</Label>
            <Input id={line1Id} {...field("addressLine1")} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor={line2Id}>Address line 2</Label>
            <Input id={line2Id} {...field("addressLine2")} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={cityId}>City</Label>
            <Input id={cityId} {...field("city")} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={pincodeId}>PIN code</Label>
            <Input id={pincodeId} {...field("pincode")} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
